import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Camera, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

interface CreateStoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStoryCreated: () => void;
}

const CreateStoryModal = ({ open, onOpenChange, onStoryCreated }: CreateStoryModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  
  const { user } = useAuth();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.type.startsWith('image/') && !selectedFile.type.startsWith('video/')) {
      toast.error('Seleziona solo immagini o video');
      return;
    }

    // Validate file size (10MB max)
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('Il file deve essere inferiore a 10MB');
      return;
    }

    setFile(selectedFile);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleUpload = async () => {
    if (!file || !user) return;

    setUploading(true);
    
    try {
      // Upload file to Supabase storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('post-images')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('post-images')
        .getPublicUrl(fileName);

      // Create story record
      const { error: storyError } = await supabase
        .from('stories')
        .insert({
          user_id: user.id,
          content_url: publicUrl,
          content_type: file.type.startsWith('image/') ? 'image' : 'video',
          caption: caption.trim() || null
        });

      if (storyError) throw storyError;

      toast.success('Storia creata con successo!');
      onStoryCreated();
      handleClose();
    } catch (error) {
      console.error('Error creating story:', error);
      toast.error('Errore nella creazione della storia');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setCaption('');
    setPreview(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md glass border-white/20">
        <DialogHeader>
          <DialogTitle className="gradient-text">Crea la tua storia</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!preview ? (
            <div className="flex flex-col items-center justify-center border-2 border-dashed border-primary/20 rounded-lg p-8 space-y-4">
              <Camera className="w-12 h-12 text-muted-foreground" />
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  Seleziona foto o video per la tua storia
                </p>
                <p className="text-xs text-muted-foreground">
                  Formati supportati: JPG, PNG, MP4 (max 10MB)
                </p>
              </div>
              <Label htmlFor="story-file" className="cursor-pointer">
                <Button variant="outline" className="glass border-white/20" asChild>
                  <span>
                    <Upload className="w-4 h-4 mr-2" />
                    Seleziona file
                  </span>
                </Button>
              </Label>
              <Input
                id="story-file"
                type="file"
                accept="image/*,video/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative">
                {file?.type.startsWith('image/') ? (
                  <img 
                    src={preview} 
                    alt="Preview" 
                    className="w-full h-64 object-cover rounded-lg"
                  />
                ) : (
                  <video 
                    src={preview} 
                    className="w-full h-64 object-cover rounded-lg"
                    controls
                  />
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFile(null);
                    setPreview(null);
                  }}
                  className="absolute top-2 right-2 w-8 h-8 p-0 bg-black/50 hover:bg-black/70 text-white"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div>
                <Label htmlFor="caption">Didascalia (opzionale)</Label>
                <Textarea
                  id="caption"
                  placeholder="Aggiungi una didascalia..."
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  className="glass border-white/20 mt-1"
                  maxLength={200}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {caption.length}/200 caratteri
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={handleClose} className="glass border-white/20">
              Annulla
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={!file || uploading}
              className="bg-gradient-to-r from-primary to-secondary"
            >
              {uploading ? 'Caricamento...' : 'Pubblica storia'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateStoryModal;